import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";

import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";

import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import SearchIcon from "@mui/icons-material/Search";
import GroupsIcon from "@mui/icons-material/Groups";
import BarChartIcon from "@mui/icons-material/BarChart";
import FlightIcon from "@mui/icons-material/Flight";
import AssessmentIcon from "@mui/icons-material/Assessment";
import LoginIcon from "@mui/icons-material/Login";

import { logout } from "../slices/auth";

const GREEN_ACCENT = "#4caf50";
const GREEN_HOVER = "rgba(76, 175, 80, 0.08)";
const HEADER_BG = "#f0f0f0";
const TEXT_COLOR = "#333";

const navItems = [
  { label: "Plant Search", to: "/search", icon: <SearchIcon fontSize="small" /> },
  { label: "Groups", to: "/groups", icon: <GroupsIcon fontSize="small" /> },
  { label: "Charts", to: "/charts", icon: <BarChartIcon fontSize="small" /> },
  { label: "Drone Data", to: "/drone", icon: <FlightIcon fontSize="small" /> },
  { label: "Reports", to: "/reports", icon: <AssessmentIcon fontSize="small" /> },
];

const navButtonSx = (isActive) => ({
  color: isActive ? GREEN_ACCENT : TEXT_COLOR,
  fontWeight: isActive ? 600 : 500,
  fontSize: "0.85rem",
  textTransform: "none",
  letterSpacing: "0.02em",
  px: 1.8,
  py: 0.8,
  borderRadius: "8px",
  position: "relative",
  transition: "all 0.2s ease",
  "&:visited": {
    color: isActive ? GREEN_ACCENT : TEXT_COLOR,
  },
  "&::after": isActive
    ? {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: "20%",
        width: "60%",
        height: "2.5px",
        backgroundColor: GREEN_ACCENT,
        borderRadius: "2px",
      }
    : {},
  "&:hover, &:hover:visited": {
    backgroundColor: GREEN_HOVER,
    color: GREEN_ACCENT,
  },
});

const Header = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const location = useLocation();

  const logOut = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const imgSrc = require("../img/logo.png");

  const isActive = (path) => location.pathname === path;

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: HEADER_BG,
        borderBottom: "1px solid #ddd",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}
    >
      <Toolbar
        sx={{
          justifyContent: "space-between",
          minHeight: { xs: 56, sm: 64 },
          px: { xs: 1, sm: 3 },
        }}
      >
        {/* Left: Logo + Nav */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Link to="/" style={{ display: "flex", alignItems: "center" }}>
            <img
              src={imgSrc.default}
              alt="logo"
              height={36}
              style={{ marginRight: 16 }}
            />
          </Link>

          {currentUser && (
            <>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ mx: 1, borderColor: "#ccc" }}
              />

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {navItems.map((item) => (
                  <Button
                    key={item.to}
                    component={Link}
                    to={item.to}
                    startIcon={item.icon}
                    disableRipple
                    sx={navButtonSx(isActive(item.to))}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            </>
          )}
        </Box>

        {/* Right: User / Auth */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {currentUser ? (
            <>
              <Chip
                icon={<AccountCircleIcon />}
                label={currentUser.username}
                component={Link}
                to="/profile"
                clickable
                variant="outlined"
                sx={{
                  fontWeight: 500,
                  fontSize: "0.82rem",
                  borderColor: "#ccc",
                  color: TEXT_COLOR,
                  "&:visited": { color: TEXT_COLOR },
                  "& .MuiChip-icon": { color: GREEN_ACCENT },
                  "&:hover, &:hover:visited": {
                    borderColor: GREEN_ACCENT,
                    backgroundColor: GREEN_HOVER,
                    color: TEXT_COLOR,
                  },
                }}
              />

              <Tooltip title="Logout" arrow>
                <IconButton
                  onClick={logOut}
                  size="small"
                  sx={{
                    color: TEXT_COLOR,
                    "&:hover": {
                      color: "#d32f2f",
                      backgroundColor: "rgba(211,47,47,0.08)",
                    },
                  }}
                >
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Button
              component={Link}
              to="/login"
              variant="contained"
              startIcon={<LoginIcon />}
              disableElevation
              sx={{
                backgroundColor: GREEN_ACCENT,
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.85rem",
                textTransform: "none",
                borderRadius: "8px",
                px: 2.5,
                "&:visited": { color: "#fff" },
                "&:hover, &:hover:visited": {
                  backgroundColor: "#43a047",
                  color: "#fff",
                },
              }}
            >
              Login
            </Button>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
